import { SurveyQuestionConfig, SurveyQuestionOption, ScoredSurveyQuestionOption, LanguageCode } from "../../DTOs";
import { languageValidation } from "../fieldValidation";
import { surveyQuestionFieldValidation } from '../fieldValidation/fields/surveyQuestionFieldValidation';


export type SurveyQuestionOptionObjectValidationResult =
      'valid' 
    | 'invalid_question_config' 
    | 'invalid_question_option' 
    | 'invalid_question_option_count' 
    | 'invalid_question_option_translation';

function validateConfigOptions(config:SurveyQuestionConfig, requiredTranslations:LanguageCode[]):SurveyQuestionOptionObjectValidationResult
{
    if(config.type !== 'custom' && config.type !== 'multiple-choice')
        return 'invalid_question_config';

    if(!Array.isArray(config.options))
        return 'invalid_question_config';

    if(config.options.length < 2)
        return 'invalid_question_option_count';

    const values = config.options.map(o => o.value);
    if(new Set(values).size !== values.length)
        return 'invalid_question_option';

    const texts = config.options.map(o => o.text);
    if(new Set(texts).size !== texts.length)
        return 'invalid_question_option';

    for(let i=0; i<config.options.length; i++)
    {
        if(config.type === 'custom' && config.scored) {
            const validation = validateScoredSurveyQuestionOption(config.options[i], requiredTranslations);
            if(validation !== 'valid')
                return validation;

        } else {
            const validation = validateUnscoredSurveyQuestionOption(config.options[i], requiredTranslations);
            if(validation !== 'valid')
                return validation;
        }
    }

    return 'valid';
}

function validateUnscoredSurveyQuestionOption(option:SurveyQuestionOption, requiredTranslations:LanguageCode[]):SurveyQuestionOptionObjectValidationResult | 'valid'
{
    const allowedFields = ['value', 'text', 'translations'];
    if(Object.keys(option).find(k => !allowedFields.includes(k)))
        return 'invalid_question_option';

    if((option as any).score !== undefined)
        return 'invalid_question_option';

    return validateBaseSurveyQuestionOption(option, requiredTranslations);
}

function validateScoredSurveyQuestionOption(option:ScoredSurveyQuestionOption, requiredTranslations:LanguageCode[]):SurveyQuestionOptionObjectValidationResult | 'valid'
{
    const allowedFields = ['value', 'text', 'translations', 'score'];
    if(Object.keys(option).find(k => !allowedFields.includes(k)))
        return 'invalid_question_option';

    if(!surveyQuestionFieldValidation.surveyQuestionOption.score(option.score))
        return 'invalid_question_option';

    return validateBaseSurveyQuestionOption(option, requiredTranslations);
}

function validateBaseSurveyQuestionOption(option:SurveyQuestionOption, requiredTranslations:LanguageCode[]):SurveyQuestionOptionObjectValidationResult | 'valid' {
    if(option === null || option === undefined || typeof option !== 'object')
        return 'invalid_question_option';

    if(!surveyQuestionFieldValidation.surveyQuestionOption.text(option.text))
        return 'invalid_question_option';

    if(!surveyQuestionFieldValidation.surveyQuestionOption.value(option.value))
        return 'invalid_question_option';

    if(!languageValidation.validateLanguageCodeList(requiredTranslations))
        throw new Error('Invalid requiredTranslations list');

    if(!languageValidation.validateLocalizedSurveyTexts(option.translations))
        return 'invalid_question_option_translation';

    const identicalTranslations = languageValidation.identicalLanguageCodes(
        requiredTranslations, 
        option.translations.map(t => t.languageCode)
    );
    if(!identicalTranslations)
        return 'invalid_question_option_translation';

    if(option.translations.some(t => !surveyQuestionFieldValidation.surveyQuestionOption.text(t.text)))
        return 'invalid_question_option';

    return 'valid';
}

export const surveyQuestionOptionObjectValidation = {
    validateConfigOptions
}